export class Queue<T> {
    private items: T[];
    private head = 0;

    constructor(initial: T[] = []) {
        this.items = initial.slice(); // copy so caller's array isn't mutated
    }

    enqueue(item: T): void {
        this.items.push(item);
    }

    dequeue(): T | undefined {
        if (this.head >= this.items.length) return undefined;

        const value = this.items[this.head++];
        // occasionally compact to avoid array growing forever
        if (this.head > 1024 && this.head * 2 > this.items.length) {
            this.items = this.items.slice(this.head);
            this.head = 0;
        }
        return value;
    }

    /**
     * Removes (dequeues) the first item matching `predicate` from anywhere in the queue.
     * Returns the removed item, or `undefined` if not found.
     */
    dequeueWhere(predicate: (item: T) => boolean): T | undefined {
        for (let i = this.head; i < this.items.length; i++) {
            const item = this.items[i];
            if (predicate(item)) {
                // fast path: removing the head behaves like a normal dequeue
                if (i === this.head) return this.dequeue();

                // remove from middle/tail
                this.items.splice(i, 1);

                // if queue became empty, normalize head
                if (this.head >= this.items.length) {
                    this.items = [];
                    this.head = 0;
                }

                return item;
            }
        }
        return undefined;
    }

    peek(): T | undefined {
        return this.items[this.head];
    }

    /**
     * Returns (without removing) the first item matching `predicate` from anywhere in the queue.
     */
    peekWhere(predicate: (item: T) => boolean): T | undefined {
        for (let i = this.head; i < this.items.length; i++) {
            const item = this.items[i];
            if (predicate(item)) return item;
        }
        return undefined;
    }

    /**
     * Returns the 0-based position of the first item matching `predicate` within the queue
     * (0 = current head). Returns -1 if not found.
     */
    positionWhere(predicate: (item: T) => boolean): number {
        for (let i = this.head; i < this.items.length; i++) {
            const item = this.items[i];
            if (predicate(item)) return i - this.head;
        }
        return -1;
    }

    get size(): number {
        return this.items.length - this.head;
    }

    get isEmpty(): boolean {
        return this.size === 0;
    }

    clear(): void {
        this.items = [];
        this.head = 0;
    }

    toArray(): T[] {
        return this.items.slice(this.head);
    }
}
